<template>
  <PTCLink
    :to="event.offSiteUrl ?? event.currentPathname ?? ''"
    :class="{
      contents: true,
    }"
  >
    <article
      :class="{
        'group relative isolate grid overflow-hidden p-6 text-www-color-white': true,
        'xl:px-12 xl:pb-6 xl:pt-10': true,
        'grid-rows-[auto_1fr_auto_auto] [grid-row:span_4] [grid-template-rows:subgrid]': true,
        'col-span-3 md:col-span-1': true,
        'bg-cover bg-right-bottom bg-no-repeat': true,
        [useEventTheme({ theme: event.theme }).eventThemeClass.value]: true,
      }"
      :style="{
        backgroundImage: `url(${useEventTheme({ theme: event.theme }).getEventBackgroundImage()})`,
      }"
    >
      <div :class="{ 'overlay group-hover:opacity-100': true }"></div>
      <div
        v-if="event.offSiteUrl || event.currentPathname"
        :class="{
          'flex items-center justify-between gap-3': true,
        }"
      >
        <span
          v-if="event.startDateTime && event.endDateTime"
          :class="{
            'date-format whitespace-nowrap rounded border border-www-color-white px-4 pb-6px pt-[7px] font-ptc-Rajdhani text-base font-bold uppercase leading-none text-www-color-white xl:text-20px': true,
            'group-hover:bg-www-color-white group-hover:text-www-color-accent-dark': true,
            'transition-all duration-300': true,
          }"
        >
          {{ event.cardTime }}
        </span>
        <span
          v-if="event.location"
          :class="{
            'flex items-center gap-10px text-base font-bold leading-none': true,
          }"
        >
          <PtcIcon
            v-if="event.eventFormat === 'virtual'"
            name="ptc-online"
          />
          <PtcIcon
            v-else
            name="ptc-location"
            :class="{
              'h-5 w-15px text-www-color-white': true,
            }"
          />
          <span v-if="event.eventFormat">{{ event.location }}</span>
        </span>
      </div>
      <h3
        :class="{
          'mt-6 text-xl font-bold leading-none text-www-color-neutral-off-white first-letter:uppercase lg:text-35px': true,
        }"
      >
        {{ event.title }}
      </h3>
      <p
        v-if="event.description"
        :title="event.description"
        :class="{
          'self-start text-sm leading-none text-www-color-neutral-off-white first-letter:uppercase lg:text-22px': true,
          'opacity-0 transition-all duration-300 group-hover:opacity-100 lg:line-clamp-5': true,
        }"
      >
        {{ event.description }}
      </p>
      <div v-else />
      <PtcPicture
        v-if="event?.illustration?.asset"
        provider="sanity"
        :src="event?.illustration.asset?._ref"
        alt="Illustration"
        :loading="'eager'"
        :preload="true"
        :img-attrs="{
          class: {
            'lg:block hidden absolute right-5 h-36 w-48 bottom-5 -z-[2]': true,
          },
        }"
      />
      <div class="mt-auto flex items-center justify-between">
        <div
          :class="{
            'flex items-end gap-2': true,
          }"
        >
          <PtcIcon
            v-if="getEventTypeIconName(event.eventType)"
            :name="getEventTypeIconName(event.eventType)"
            :class="{
              'h-5 w-0 shrink-0 text-www-color-white': true,
              'opacity-0 transition-all duration-300 group-hover:w-30px group-hover:opacity-100': true,
            }"
          />
          <span class="text-base font-bold uppercase leading-none">
            {{ event.eventType ?? 'Event' }}
          </span>
        </div>
        <div
          :class="{
            'h-10 w-10 cursor-pointer rounded-full bg-www-color-white text-xs font-semibold underline': true,
            'flex items-center justify-center': true,
            'opacity-100 transition-all duration-300 lg:opacity-0 group-hover:lg:opacity-100': true,
          }"
        >
          <PtcIcon
            name="ptc-arrow-white"
            :class="{ 'text-lg text-www-color-accent-dark': true }"
          />
        </div>
      </div>
    </article>
  </PTCLink>
</template>

<script setup lang="ts">
import PtcIcon from '~/components/Atmos/PtcIcon/PtcIcon.vue';
import PTCLink from '~/components/Atmos/PTCLink/PTCLink.vue';
import PtcPicture from '~/components/Atmos/PtcPicture/PtcPicture.vue';

import type { CardBlogResult, GenericEvent } from '~/sanity/types';

defineProps<{
  event: NonNullable<CardBlogResult>;
}>();

// add PTC icons depends on event type
const eventTypeIconNameMap: Record<GenericEvent['eventType'], string> = {
  webinar: 'ptc-event-webinar',
  tradeShow: 'ptc-tradeShow',
  conference: 'ptc-conference',
  meetup: 'ptc-conference',
};

const getEventTypeIconName = (eventType: GenericEvent['eventType'] | null): string => {
  if (!eventType) {
    return '';
  }
  return eventTypeIconNameMap[eventType];
};
</script>

<style lang="scss" scoped>
.overlay {
  background: var(--www-color-accent-dark);
  position: absolute;
  inset: 0;
  z-index: -1;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}
</style>
